import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { createAppTheme } from '../theme';

// Tryb jasny/ciemny: wybór użytkownika w localStorage, a bez wyboru – ustawienie systemu.
// Atrybut data-theme na <html> przełącza zmienne CSS z index.css; ten sam tryb dostaje motyw MUI.

const STORAGE_KEY = 'themeMode';
const ThemeModeContext = createContext({ mode: 'light', toggleMode: () => {} });

const readStoredMode = () => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored === 'dark' || stored === 'light' ? stored : null;
    } catch (error) {
        return null;
    }
};

const systemPrefersDark = () =>
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches;

export function ThemeModeProvider({ children }) {
    const [storedMode, setStoredMode] = useState(readStoredMode);
    const [systemDark, setSystemDark] = useState(systemPrefersDark);
    const mode = storedMode || (systemDark ? 'dark' : 'light');

    // Zmiana ustawień systemu działa, dopóki użytkownik sam nie wybrał trybu
    useEffect(() => {
        const media = window.matchMedia?.('(prefers-color-scheme: dark)');
        if (!media) return undefined;
        const handleChange = (event) => setSystemDark(event.matches);
        media.addEventListener?.('change', handleChange);
        return () => media.removeEventListener?.('change', handleChange);
    }, []);

    useEffect(() => {
        document.documentElement.dataset.theme = mode;
    }, [mode]);

    const toggleMode = useCallback(() => {
        const next = mode === 'dark' ? 'light' : 'dark';
        setStoredMode(next);
        try {
            localStorage.setItem(STORAGE_KEY, next);
        } catch (error) {
            // Bez dostępu do localStorage tryb działa do odświeżenia strony
        }
    }, [mode]);

    const theme = useMemo(() => createAppTheme(mode), [mode]);
    const value = useMemo(() => ({ mode, toggleMode }), [mode, toggleMode]);

    return (
        <ThemeModeContext.Provider value={value}>
            <ThemeProvider theme={theme}>{children}</ThemeProvider>
        </ThemeModeContext.Provider>
    );
}

export const useThemeMode = () => useContext(ThemeModeContext);
